/* ------------------------------------------------------------------
 *  Navbar — Édition "Éditorial Academy"
 *  Hairline marine, typo Fraunces sur le wordmark, liens en eyebrow
 *  uppercase + gold-underline animé pour l'actif.
 * ------------------------------------------------------------------ */
import { useState, useEffect } from "react"
import { Link, useLocation } from "react-router-dom"
import { Menu, X, User, LogOut, ArrowUpRight } from "lucide-react"
import { useAuth } from "../hooks/useAuth"
import { useTranslation } from "../i18n"
import LanguageSwitcher from "./LanguageSwitcher"
import Avatar from "./Avatar"

export default function Navbar() {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [isProfileOpen, setIsProfileOpen] = useState(false)
  const [isScrolled, setIsScrolled] = useState(false)
  const location = useLocation()
  const { isAuthenticated, user, logout } = useAuth()
  const { t } = useTranslation()

  /* Scroll → ombre subtile */
  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 8)
    window.addEventListener("scroll", onScroll)
    return () => window.removeEventListener("scroll", onScroll)
  }, [])

  /* Bloquer scroll quand menu mobile ouvert */
  useEffect(() => {
    if (isMenuOpen) {
      const y = window.scrollY
      document.body.style.position = "fixed"
      document.body.style.top = `-${y}px`
      document.body.style.width = "100%"
      document.body.style.overflow = "hidden"
    } else {
      const y = document.body.style.top
      document.body.style.position = ""
      document.body.style.top = ""
      document.body.style.width = ""
      document.body.style.overflow = ""
      if (y) window.scrollTo(0, parseInt(y || "0") * -1)
    }
    return () => {
      document.body.style.position = ""
      document.body.style.top = ""
      document.body.style.width = ""
      document.body.style.overflow = ""
    }
  }, [isMenuOpen])

  const navigationItems = [
    { key: "home",     href: "/",         translationKey: "nav.home" },
    { key: "about",    href: "/about",    translationKey: "nav.about" },
    { key: "courses",  href: "/courses",  translationKey: "nav.courses" },
    { key: "webinars", href: "/webinars", translationKey: "nav.webinars" },
    { key: "faq",      href: "/faq",      translationKey: "nav.faq" },
    { key: "contact",  href: "/contact",  translationKey: "nav.contact" },
  ]

  const isItemActive = (item) => {
    if (item.key === "webinars") return location.pathname.startsWith("/webinars")
    if (item.key === "faq")      return location.pathname === "/faq"
    if (item.key === "home")     return location.pathname === "/"
    return location.pathname === item.href
  }

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 bg-[#203a8a] text-white border-b transition-all duration-300 ${
        isScrolled
          ? "border-white/15 shadow-[0_4px_24px_-12px_rgba(0,0,0,0.45)]"
          : "border-transparent"
      }`}
    >
      <div className="edito-container">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <a
            href="https://ay-solutions.org"
            rel="noopener noreferrer"
            className="flex-shrink-0 flex items-center gap-3 group"
            title="AY Solutions Academy"
          >
            <img
              src="/images/logo.png"
              alt="AY Solutions Academy"
              className="h-12 md:h-14 w-auto object-contain"
            />
            <span className="hidden sm:inline-block font-display text-white text-lg leading-none tracking-tight">
              <span className="block text-[10px] uppercase tracking-[0.3em] text-[#E8C547] font-sans mb-0.5">
                AY Solutions
              </span>
              Academy
            </span>
          </a>

          {/* Navigation Desktop */}
          <div className="hidden md:flex items-center gap-7">
            {navigationItems.map((item) => {
              const active = isItemActive(item)
              return (
                <Link
                  key={item.key}
                  to={item.href}
                  className="relative group text-[11px] uppercase tracking-[0.22em] font-medium transition-colors"
                  style={{ color: active ? "#E8C547" : "rgba(255,255,255,0.85)" }}
                >
                  {t(item.translationKey)}
                  <span
                    className="absolute -bottom-1.5 left-0 right-0 h-[2px] bg-[#E8C547] transition-transform duration-500 origin-left"
                    style={{ transform: active ? "scaleX(1)" : "scaleX(0)" }}
                  />
                  <span className="absolute -bottom-1.5 left-0 right-0 h-[2px] bg-[#E8C547]/60 transition-transform duration-500 origin-left scale-x-0 group-hover:scale-x-100" />
                </Link>
              )
            })}
          </div>

          {/* User / CTA Desktop */}
          <div className="hidden md:flex items-center gap-3">
            <LanguageSwitcher />
            {isAuthenticated ? (
              <div className="flex items-center gap-3">
                {(user?.role === "admin" ||
                  user?.role === "ADMIN" ||
                  user?.isAdmin) && (
                  <Link
                    to="/admin/dashboard"
                    className="inline-flex items-center gap-2 bg-white text-[#203a8a] hover:bg-[#E8C547] px-4 py-2 text-xs uppercase tracking-[0.22em] font-medium transition-colors"
                  >
                    {t("nav.dashboard")}
                    <ArrowUpRight className="w-3.5 h-3.5" />
                  </Link>
                )}
                {/* Profile menu */}
                <div className="relative">
                  <button
                    onClick={() => setIsProfileOpen(!isProfileOpen)}
                    className="flex items-center gap-2 focus:outline-none"
                  >
                    <Avatar
                      src={user?.avatar}
                      name={user?.name}
                      size={40}
                      className="flex-shrink-0 ring-1 ring-white/30 hover:ring-[#E8C547] transition-all"
                    />
                  </button>
                  {isProfileOpen && (
                    <>
                      <div
                        className="fixed inset-0 z-40"
                        onClick={() => setIsProfileOpen(false)}
                      />
                      <div className="absolute right-0 mt-3 w-56 bg-white border border-[#D5DAE3] shadow-[0_24px_60px_-28px_rgba(27,34,64,0.35)] z-50">
                        {user?.name && (
                          <div className="px-4 py-3 border-b border-[#D5DAE3]">
                            <span className="text-[10px] uppercase tracking-[0.25em] text-[#6B7280]">
                              Connecté
                            </span>
                            <p className="font-display text-base text-[#203a8a] mt-0.5 truncate">
                              {user.name}
                            </p>
                          </div>
                        )}
                        <Link
                          to="/profile"
                          className="flex items-center gap-3 px-4 py-3 text-sm text-[#1B2240] hover:bg-[#F1F3F8] transition-colors"
                          onClick={() => setIsProfileOpen(false)}
                        >
                          <User className="w-4 h-4" />
                          <span>{t("nav.profile")}</span>
                        </Link>
                        <button
                          onClick={() => {
                            logout()
                            setIsProfileOpen(false)
                          }}
                          className="flex items-center gap-3 w-full px-4 py-3 text-sm text-[#1B2240] hover:bg-[#F1F3F8] hover:text-[#DC2626] transition-colors border-t border-[#D5DAE3]"
                        >
                          <LogOut className="w-4 h-4" />
                          <span>{t("nav.logout")}</span>
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <Link
                  to="/login"
                  className="px-4 py-2 text-xs uppercase tracking-[0.22em] font-medium border border-white/40 text-white hover:bg-white hover:text-[#203a8a] transition-colors"
                >
                  {t("nav.login")}
                </Link>
                <Link
                  to="/signup"
                  className="inline-flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-[0.22em] font-medium bg-[#E8C547] text-[#203a8a] hover:bg-[#d4af3a] transition-colors"
                >
                  {t("nav.signup")}
                  <ArrowUpRight className="w-3.5 h-3.5" />
                </Link>
              </div>
            )}
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden flex items-center gap-2">
            <LanguageSwitcher />
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="inline-flex items-center justify-center p-2 text-white hover:text-[#E8C547] focus:outline-none"
            >
              {isMenuOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {isMenuOpen && (
        <div className="md:hidden fixed inset-0 z-50">
          <div
            className="fixed inset-0 bg-[#0d1430]/60 backdrop-blur-sm"
            onClick={() => setIsMenuOpen(false)}
          />
          <div className="fixed right-0 top-0 h-full w-full sm:w-96 bg-[#FAFBFD] shadow-2xl flex flex-col">
            {/* Header menu */}
            <div className="flex items-center justify-between p-5 border-b border-[#D5DAE3]">
              <span className="text-[10px] uppercase tracking-[0.28em] text-[#6B7280]">
                {t("nav.menu")}
              </span>
              <button
                onClick={() => setIsMenuOpen(false)}
                className="p-2 text-[#1B2240] hover:text-[#203a8a]"
              >
                <X size={22} />
              </button>
            </div>

            {/* Navigation */}
            <div className="flex-1 px-5 py-6 overflow-y-auto">
              <ul className="divide-y divide-[#D5DAE3] border-y border-[#D5DAE3]">
                {navigationItems.map((item) => {
                  const active = isItemActive(item)
                  return (
                    <li key={item.key}>
                      <Link
                        to={item.href}
                        onClick={() => setIsMenuOpen(false)}
                        className="flex items-center justify-between py-4 group"
                      >
                        <span
                          className="font-display text-2xl leading-tight"
                          style={{ color: active ? "#203a8a" : "#1B2240" }}
                        >
                          {t(item.translationKey)}
                        </span>
                        <ArrowUpRight
                          className="w-5 h-5 transition-all duration-500 opacity-40 group-hover:opacity-100 group-hover:translate-x-1 group-hover:-translate-y-1"
                          style={{
                            color: active ? "#E8C547" : "#203a8a",
                            opacity: active ? 1 : undefined,
                          }}
                        />
                      </Link>
                    </li>
                  )
                })}
              </ul>
            </div>

            {/* User section */}
            <div className="p-5 border-t border-[#D5DAE3] space-y-3">
              {isAuthenticated ? (
                <>
                  {(user?.role === "admin" ||
                    user?.role === "ADMIN" ||
                    user?.isAdmin) && (
                    <Link
                      to="/admin/dashboard"
                      onClick={() => setIsMenuOpen(false)}
                      className="flex items-center justify-center gap-2 w-full bg-[#203a8a] text-white hover:bg-[#1a2f6e] py-3 text-xs uppercase tracking-[0.22em] font-medium transition-colors"
                    >
                      {t("nav.dashboard")}
                      <ArrowUpRight className="w-4 h-4" />
                    </Link>
                  )}
                  <Link
                    to="/profile"
                    onClick={() => setIsMenuOpen(false)}
                    className="flex items-center gap-3 px-3 py-3 border border-[#D5DAE3] hover:border-[#203a8a] transition-colors"
                  >
                    <Avatar src={user?.avatar} name={user?.name} size={40} />
                    <div className="flex-1 min-w-0">
                      <span className="text-[10px] uppercase tracking-[0.25em] text-[#6B7280]">
                        Profil
                      </span>
                      <p className="font-display text-base text-[#203a8a] truncate">
                        {user?.name || t("nav.profile")}
                      </p>
                    </div>
                  </Link>
                  <button
                    onClick={() => {
                      logout()
                      setIsMenuOpen(false)
                    }}
                    className="flex items-center justify-center gap-2 w-full py-3 text-xs uppercase tracking-[0.22em] font-medium text-[#DC2626] border border-[#D5DAE3] hover:border-[#DC2626] transition-colors"
                  >
                    <LogOut className="w-4 h-4" />
                    {t("nav.logout")}
                  </button>
                </>
              ) : (
                <div className="grid grid-cols-2 gap-2">
                  <Link
                    to="/login"
                    onClick={() => setIsMenuOpen(false)}
                    className="px-4 py-3 text-xs uppercase tracking-[0.22em] font-medium border border-[#203a8a] text-[#203a8a] text-center hover:bg-[#203a8a] hover:text-white transition-colors"
                  >
                    {t("nav.login")}
                  </Link>
                  <Link
                    to="/signup"
                    onClick={() => setIsMenuOpen(false)}
                    className="px-4 py-3 text-xs uppercase tracking-[0.22em] font-medium bg-[#E8C547] text-[#203a8a] text-center hover:bg-[#d4af3a] transition-colors"
                  >
                    {t("nav.signup")}
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </nav>
  )
}
